// Anita Week 2 / Day 2 — Measurements and Little Workshop (U.S. Grade 3).
// Math: ribbons × cards + remainder, paper "need vs have", perimeter of a
// rectangle, comparing two perimeters of different shapes. Russian RU 1–2.
// Logic: find Nimny's mistake about longer = always more perimeter.

const day2 = {
  id: 'day-2',
  studentId: 'anita',
  weekId: 'week-2',
  dayId: 'day-2',
  status: 'active',
  title: 'Измерения и маленькая мастерская / Measurements and Little Workshop',
  shortTitle: 'Измерения / Measurements',
  theme:
    'Сантиметры помогают делать поделки точно. Разные формы могут иметь одинаковый периметр. / Centimeters help make crafts accurate. Different shapes can have the same perimeter.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни открывает маленькую мастерскую. Сегодня Анита измеряет ленту и бумагу, считает периметр и сравнивает две открытки. / Nimny opens a little workshop. Today Anita measures ribbon and paper, finds perimeter, and compares two cards.',
  goals: [
    'Считать длину и остаток ленты в две операции. / Compute ribbon length and remainder in two steps.',
    'Сравнивать «нужно / есть» для бумаги. / Compare "need / have" for paper.',
    'Считать периметр прямоугольника двумя способами. / Find a rectangle perimeter two ways.',
    'Сравнивать периметры двух разных форм. / Compare perimeters of two different shapes.',
    'Найти ошибку Нимни про «длиннее = больше периметр». / Find Nimny\'s mistake.'
  ],
  vocabulary: [
    { term: 'длина / length', definition: 'насколько вещь длинная. / how long a thing is.' },
    { term: 'ширина / width', definition: 'насколько вещь широкая. / how wide a thing is.' },
    { term: 'лист / sheet', definition: 'бумажный лист для поделки. / a sheet of paper.' },
    { term: 'лента / ribbon', definition: 'тонкая полоска для украшения. / a thin strip for decoration.' },
    { term: 'открытка / card', definition: 'небольшой бумажный листок с рисунком. / a small paper card.' },
    { term: 'периметр / perimeter', definition: 'сумма всех сторон фигуры. / sum of all sides of a shape.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words', type: 'text',
      title: 'Слова дня / Words of the Day',
      prompt: 'Прочитай: длина, ширина, лист, лента, открытка, периметр.',
      ruPrimaryText: 'Прочитай по-русски: длина, ширина, лист, лента, открытка, периметр.',
      enHelpText: 'Read in Russian: length, width, sheet, ribbon, card, perimeter.',
      hint: 'Сначала русское чтение. / Russian first.',
      answer: 'Попытка чтения 6 русских слов.'
    },
    {
      id: 'reading-sentences', type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\n1. Сантиметры помогают мне делать поделки точно.\n2. Я считаю длину и ширину.\n3. Периметр — это сумма всех сторон.\n4. Две разные формы могут иметь одинаковый периметр.',
      ruPrimaryText: 'Прочитай 4 короткие фразы про измерения.',
      enHelpText:
        '1. Centimeters help me make crafts accurate.\n2. I count length and width.\n3. Perimeter = sum of all sides.\n4. Two different shapes can have the same perimeter.',
      answer: 'Попытка чтения 4 русских фраз.'
    },
    {
      id: 'math-1-ribbons',
      type: 'math',
      title: 'Math 1 — Ленты для закладок / Ribbons for Bookmarks',
      prompt:
        'Анита делает 4 закладки.\nНа каждую закладку нужно 12 см ленточки.\nУ неё есть 60 см ленточки.\nСколько сантиметров понадобится?\nСколько сантиметров останется?\n\nЗапиши ответ через /: нужно / останется. Например: 48 / 12.',
      ruPrimaryText: '4 × 12 = ?. 60 − ? = ?. Ответ через /: нужно / останется.',
      enHelpText: '4 bookmarks × 12 cm. Have 60 cm. Need? Remain? Format as need / remain.',
      helper: 'Сначала 4 × 12, потом 60 минус. / Multiply first, then subtract.',
      answer: '48 / 12'
    },
    {
      id: 'math-2-paper-need-have',
      type: 'math',
      title: 'Math 2 — Хватит ли бумаги / Will the Paper Be Enough',
      prompt:
        'Для одной открытки нужно 2 листа бумаги.\nАнита хочет сделать 7 открыток.\nУ неё есть 12 листов.\nХватит ли бумаги?\nСколько листов не хватает или остаётся?\n\nЗапиши ответ через /: хватит-или-нет / число. Например: не хватит / 2.',
      ruPrimaryText: '2 × 7 = ?. Есть 12. Хватит ли? На сколько?',
      enHelpText: '2 sheets × 7 cards. Have 12. Enough? Format as enough-or-not / number.',
      helper: 'Сначала нужно для 7 открыток, потом сравни с 12. / Need first, then compare.',
      answer: 'не хватит / 2'
    },
    {
      id: 'math-3-card-perimeter',
      type: 'math',
      title: 'Math 3 — Периметр открытки / Card Perimeter',
      prompt:
        'Открытка имеет форму прямоугольника.\nДлина 14 см, ширина 9 см.\nНайди периметр.',
      ruPrimaryText: 'Периметр прямоугольника 14 × 9 = ?',
      enHelpText: 'A rectangle 14 cm by 9 cm. Find the perimeter.',
      helper: 'Периметр = 2 × (длина + ширина). / Perimeter = 2 × (length + width).',
      answer: '46',
      unit: 'см / cm'
    },
    {
      id: 'math-4-compare-perimeters',
      type: 'math',
      title: 'Math 4 — Сравни два размера / Compare Two Sizes',
      prompt:
        'Открытка A: длина 12 см, ширина 8 см.\nОткрытка B: длина 10 см, ширина 10 см.\nУ какой открытки периметр больше?',
      ruPrimaryText: 'A 12 × 8, B 10 × 10. У кого периметр больше?',
      enHelpText: 'A: 12 × 8. B: 10 × 10. Which has a bigger perimeter?',
      helper: 'Посчитай оба периметра, потом сравни. / Compute both, then compare.',
      answer: 'периметры одинаковые — 40 см / equal perimeters — 40 cm'
    },
    {
      id: 'logic-find-nimny-perimeter-mistake',
      type: 'text',
      title: 'Логика — найди ошибку Нимни / Logic — Find Nimny\'s Mistake',
      prompt:
        'Нимни сказала: «Если открытка длиннее, её периметр всегда больше.»\nНайди ошибку и объясни 1–2 предложениями.',
      ruPrimaryText: 'Нимни ошиблась. Длиннее ≠ больше периметр. Объясни.',
      enHelpText: 'Nimny said: "If a card is longer, its perimeter is always bigger." Find the mistake and explain in 1–2 sentences.',
      placeholder: 'Это неправильно, потому что ...',
      answer: 'Принимается ответ, где ребёнок объясняет, что периметр зависит и от длины, и от ширины (и приводит пример A 12×8 и B 10×10).'
    },
    {
      id: 'spelling-measure',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt: 'Напиши: длина, ширина, лист, лента.\nВставь буквы: дли_а, ш_рина, ли_т, лен_а.',
      ruPrimaryText: 'Напиши слова и вставь буквы.',
      enHelpText: 'Write and fill in: длина, ширина, лист, лента.',
      answer: 'длина, ширина, лист, лента.'
    },
    {
      id: 'creative-my-card',
      type: 'text',
      title: 'Моя открытка / My Card',
      prompt:
        'Нарисуй открытку-прямоугольник. Подпиши длину и ширину.\nВарианты:\n— 12 × 8 см\n— 10 × 10 см\n— 14 × 6 см\nПосчитай периметр и подпиши.',
      ruPrimaryText: 'Нарисуй прямоугольник и посчитай периметр.',
      enHelpText: 'Draw a rectangle, label length × width, compute the perimeter.',
      answer: 'Open-ended. Принимается, если есть рисунок, подписи длина/ширина и подсчитанный периметр.'
    },
    {
      id: 'speech-explain-perimeter',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи по-русски: «Периметр — это …».',
      ruPrimaryText: 'Скажи по-русски одну фразу про периметр.',
      enHelpText: 'Say in Russian: "Perimeter is …".',
      answer: 'Принимается фраза, где ребёнок говорит, что периметр — сумма всех сторон.'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши 6 слов дня.',
    '2. Реши Math 1–4 с шагами.',
    '3. Найди ошибку Нимни и объясни.',
    '4. Напиши 4 слова на правописание.',
    '5. Нарисуй прямоугольную открытку и посчитай периметр.',
    '6. Скажи родителю: «Периметр — это …».'
  ],
  printWorksheet: {
    title: 'Измерения и маленькая мастерская / Measurements and Little Workshop',
    subtitle: 'Неделя 2 · День 2 / Week 2 · Day 2',
    pages: [
      {
        title: 'Маршрут дня · Слова · Чтение / Route, Words and Reading',
        blocks: [
          { type: 'text', text: 'Миссия: Анита измеряет, считает периметр и сравнивает формы. / Mission: Anita measures, finds perimeter, and compares shapes.' },
          { type: 'list', items: [
            'Слова дня: длина, ширина, лист, лента, открытка, периметр. / Words.',
            'Маленькое чтение — 4 короткие фразы про измерения (см. экран). / Reading.',
            'English help мелким шрифтом. / English help.'
          ] }
        ]
      },
      {
        title: 'Математика измерений / Measurement Math',
        blocks: [
          { type: 'text', text: 'Показывай шаги и единицы (см). / Show steps and units (cm).' },
          { type: 'list', items: [
            'Math 1. 4 × 12 = ___ см нужно; 60 − ___ = ___ см останется. / Ribbon math.',
            'Math 2. 2 × 7 = ___ листов нужно; есть 12; ___ − 12 = ___ не хватает. / Paper need vs have.',
            'Math 3. Прямоугольник 14 × 9. Периметр = 2 × (14 + 9) = ___ см. / Card perimeter.',
            'Math 4. A 12 × 8: 2 × (12 + 8) = ___ см. B 10 × 10: 2 × (10 + 10) = ___ см. У кого больше? ___ / Compare two perimeters.',
            'Логика: «Длиннее = всегда больше периметр» — где ошибка? Ответ 1–2 предложения. / Logic.'
          ] }
        ]
      },
      {
        title: 'Правописание, моя открытка-проект и parent check / Spelling, My Card-Project and Parent Check',
        blocks: [
          { type: 'list', items: [
            'Правописание: напиши длина, ширина, лист, лента. Вставь буквы: дли_а, ш_рина, ли_т, лен_а. / Spelling.',
            'Нарисуй мою открытку-проект (прямоугольник). Подпиши длину и ширину, посчитай периметр и подпиши: «Периметр = ___ см». / Draw a card-project rectangle and compute the perimeter.',
            'Скажи родителю: «Периметр — это сумма всех сторон». / Say in Russian to a parent.',
            'Parent Check: × и сравнение «нужно/есть», периметр двумя способами, два разных прямоугольника с одинаковым периметром. / Parent Check.',
            'Notebook Mode fallback. / Notebook Mode fallback.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal: 'Анита считает длину и периметр и понимает, что разные формы могут иметь одинаковый периметр.',
    mathGoal: 'U.S. Grade 3: × как первый шаг (4 × 12, 2 × 7), сравнение «нужно/есть», периметр прямоугольника двумя способами, сравнение двух периметров.',
    russianGoal: 'Прочитать слова: длина, ширина, лист, лента, открытка, периметр.',
    readingWritingGoal: 'Прочитать 4 фразы, написать 4 слова, нарисовать прямоугольник с периметром.',
    practicalGoal: 'Найти ошибку Нимни про «длиннее = больше периметр».',
    whatThisLessonTeaches: 'Анита учится точному счёту в сантиметрах и видит, что периметр зависит и от длины, и от ширины.',
    keyMessage: 'Периметр — это сумма всех сторон. Разные формы могут иметь одинаковый периметр.',
    howToExplain: 'Возьмите линейку и две бумажные открытки 12 × 8 и 10 × 10. Покажите, что обе имеют периметр 40 см.',
    exampleDialogue: 'Parent: Сколько сантиметров уйдёт на 4 закладки по 12 см?\nChild: 48.\nParent: А останется?\nChild: 60 − 48 = 12.',
    readingSupport: 'Слова «ширина» и «периметр» — по слогам.',
    russianSpeechGoal: 'Анита говорит: «Периметр — это сумма всех сторон.»',
    mathAnswerKey:
      'Math 1: 4 × 12 = 48 см; 60 − 48 = 12 см.\nMath 2: 2 × 7 = 14 листов; есть 12; не хватит 14 − 12 = 2 листа.\nMath 3: 2 × (14 + 9) = 46 см.\nMath 4: A 2 × (12 + 8) = 40 см; B 2 × (10 + 10) = 40 см; периметры одинаковые.',
    spellingAnswerKey: 'длина\nширина\nлист\nлента',
    answerKey: [
      { taskId: 'reading-words', answer: 'Answer: попытка чтения 6 русских слов.\nSolution: Анита читает по очереди.\nParent note: Засчитывать честную попытку.' },
      { taskId: 'reading-sentences', answer: 'Answer: попытка чтения 4 фраз про измерения.\nSolution: Каждая фраза — отдельно.\nParent note: Английский — только для смысла.' },
      { taskId: 'math-1-ribbons', answer: 'Answer: 48 / 12.\nSolution: 4 × 12 = 48; 60 − 48 = 12.\nParent note: Умножение + остаток.' },
      { taskId: 'math-2-paper-need-have', answer: 'Answer: не хватит / 2.\nSolution: 2 × 7 = 14; 14 − 12 = 2.\nParent note: «Нужно / есть / разница».' },
      { taskId: 'math-3-card-perimeter', answer: 'Answer: 46 см.\nSolution: 14 + 9 + 14 + 9 = 46 или 2 × (14 + 9) = 46.\nParent note: Для Grade 3 можно принимать оба способа.' },
      { taskId: 'math-4-compare-perimeters', answer: 'Answer: периметры одинаковые — 40 см.\nSolution: A = 40, B = 40.\nParent note: Разные формы могут иметь одинаковый периметр.' },
      { taskId: 'logic-find-nimny-perimeter-mistake', answer: 'Answer: длиннее ≠ больше периметр.\nSolution: Периметр зависит и от длины, и от ширины. Пример: 12 × 8 и 10 × 10 — оба 40 см.\nParent note: Принимается любое объяснение с этой идеей.' },
      { taskId: 'spelling-measure', answer: 'Answer: длина, ширина, лист, лента.\nSolution: Вставленные буквы: н, и, с, т.\nParent note: Мягкие ошибки допустимы.' },
      { taskId: 'creative-my-card', answer: 'Answer: рисунок прямоугольника + подписи + периметр.\nSolution: Любой прямоугольник из списка вариантов.\nParent note: Главное — подписи длины/ширины и правильный периметр.' },
      { taskId: 'speech-explain-perimeter', answer: 'Answer: «Периметр — это сумма всех сторон.»\nSolution: Любая русская фраза с этой идеей.\nParent note: Главное — мысль.' }
    ],
    check: [
      'Анита прочитала 6 слов и 4 фразы.',
      'Анита решила Math 1–4 с показом шагов.',
      'Анита нашла ошибку Нимни.',
      'Анита написала 4 слова и нарисовала открытку с периметром.',
      'Анита сказала фразу про периметр.'
    ],
    spellingCheck: 'Проверить буквы в словах длина, ширина, лист, лента.',
    readingCheck: 'Засчитывать попытку чтения.',
    practicalCheck: 'Анита понимает, что периметр зависит от длины И ширины.',
    drawingCriteria: 'Прямоугольник имеет подписи длины и ширины и правильный периметр.',
    commonMistakes: [
      '«Периметр = длина × ширина.» Правильно: периметр = сумма всех сторон.',
      '«Длиннее = больше периметр.» Не всегда: 12 × 8 и 10 × 10 имеют одинаковый периметр.',
      '«1 листа хватит на 1 открытку.» В нашей задаче нужно 2 листа.'
    ],
    discussionPrompts: [
      'Что такое периметр своими словами?',
      'Когда форма длиннее, но периметр меньше?',
      'Зачем нам считать сантиметры?'
    ],
    ifEasy: 'Найди две разные прямоугольные открытки с периметром 36 см. (Например 12 × 6 или 9 × 9.)',
    ifDifficult: 'Оставьте Math 1 и Math 3. Math 3 разберите вместе по сторонам.',
    rewardGuidance: 'Базовая награда 3 нима. Бонус за оба способа периметра или за объяснение ошибки Нимни.',
    parentTheoryCard: {
      title: 'Длина и периметр (Day 2) / Length and Perimeter',
      purpose: 'Научить ребёнка измерять и складывать стороны фигуры.',
      keyMessage: 'Периметр — сумма всех сторон. Длиннее ≠ больше периметр.',
      parentScript: 'Возьмите линейку. Покажите, что 12 + 8 + 12 + 8 = 40, и 10 + 10 + 10 + 10 = 40.',
      practiceSteps: ['Прочитайте слова дня.', 'Решите Math 1–4 со шагами.', 'Найдите ошибку Нимни.', 'Нарисуйте прямоугольник с периметром.'],
      doNotSay: [
        'Не сравнивайте ребёнка с другими по точности.',
        'Не ругайте за пропущенную сторону.',
        'Не говорите «это слишком легко».'
      ],
      paperOnlyNotes: 'Все измерения — на бумаге или маленькой реальной открытке. Не использовать ножницы без взрослого.'
    },
    parentOnlyNotes: 'Можно дать линейку — это безопасно. Маленькие ножницы — только с взрослым.',
    tokenReward: 'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim', unitName: 'ним', base: 3, max: 5,
    label: 'Награда за миссию', display: '3 нима',
    bonuses: [
      { id: 'perimeter-both-ways', label: 'Бонус за оба способа подсчёта периметра', amount: 1 },
      { id: 'found-nimny-mistake', label: 'Бонус за объяснение ошибки Нимни', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words', 'reading-sentences',
      'math-1-ribbons', 'math-2-paper-need-have',
      'math-3-card-perimeter', 'math-4-compare-perimeters',
      'logic-find-nimny-perimeter-mistake', 'spelling-measure',
      'creative-my-card', 'speech-explain-perimeter'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-2-day-3'
};

export default day2;
